import * as React from 'react';

import { cn } from '@/lib/cn';
import { Card } from './card';
import { useSkeletonClock } from './skeleton-clock';

/**
 * Placeholder block while content loads. Size it with `className`
 * (`h-4 w-3/5`, `aspect-[16/10]`). The redesigned kit (kit-next.css) swaps the
 * pulse for a shimmer that reduced motion turns off, and fades the block in
 * after ~100 ms so an instant load never flashes it.
 *
 * `shape` picks the corner of what it stands in for: `control` (default: text
 * lines, fields), `card` (a card or tray) or `pill` (an avatar or dot). The
 * kit sets the radius from it; a `rounded-*` class would lose to the kit rule.
 *
 * A block-level `span`, so it can stand in for text inside a `<p>` or a heading.
 * Its animations join the shared skeleton clock, so a skeleton that replaces
 * another one continues the same fade and shimmer (`skeleton-clock.ts`).
 */
export function Skeleton({
  className,
  shape = 'control',
  ...props
}: Omit<React.ComponentProps<'span'>, 'ref'> & { shape?: 'control' | 'card' | 'pill' }) {
  const ref = useSkeletonClock<HTMLSpanElement>();
  return (
    <span
      data-slot="skeleton"
      data-shape={shape}
      aria-hidden="true"
      className={cn('block animate-pulse rounded-control bg-surface-sunken', className)}
      {...props}
      ref={ref}
    />
  );
}

/**
 * A tour card while the gallery loads, with the height of the real one: thumbnail,
 * name, collection, panorama count and, from `lg`, the row of actions.
 * `compact` is the gallery's smallest size (tighter padding).
 * `announce={false}` when it sits inside a group that already announces itself
 * (a `PageSkeleton`): one `role="status"` per group, not one per card.
 */
export function SkeletonCard({
  className,
  announce = true,
  compact = false,
}: {
  className?: string;
  announce?: boolean;
  compact?: boolean;
}) {
  return (
    <Card
      {...(announce ? { role: 'status', 'aria-label': 'Cargando' } : { 'aria-hidden': true })}
      className={cn('flex flex-col p-2 shadow-none', className)}
    >
      <Skeleton className="aspect-[16/10] w-full" />
      <div className={cn('flex flex-col gap-1.5 px-1.5', compact ? 'pb-2 pt-2.5' : 'pb-3 pt-3')}>
        <span className="flex h-5 items-center">
          <Skeleton className="h-3.5 w-3/5" />
        </span>
        <span className="flex h-4 items-center">
          <Skeleton className="h-3 w-2/5" />
        </span>
        <span className="flex h-4 items-center">
          <Skeleton className="h-3 w-1/4" />
        </span>
      </div>
      <div className="mx-1 hidden items-center justify-end gap-1 border-t border-line pt-1.5 lg:flex">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} shape="pill" className="size-8" />
        ))}
      </div>
    </Card>
  );
}
